// @flow
// $FlowFixMe - Export of includes not found even though it exists
import { includes, none, all, map, compose, partial, reduce, inc, flatten } from "ramda"
import { isAfter } from "date-fns"
import Passenger from "@kiwicom/frontend-app/scripts/containers/mutable/Passenger"

import { INTERNATIONAL_PASSPORT_NATIONALITIES } from "common/consts/passengers"
import type { CheckIn, CheckInStatus, CheckInSegment } from "../../../types/internal"
import CHECKIN_STATUSES from "../../../constants"
import type { CompletionPassenger } from "../types"
import type JourneyType from "common/records/Journey"

const {
  PROVIDE_DETAILS,
  WAITING_FOR_DETAILS,
  RESTRICTED,
  UNAVAILABLE,
  GROUND_UNAVAILABLE,
  MISSED,
  FAILED,
} = CHECKIN_STATUSES

const getStatusesForSegment = ({ passengers }: CheckInSegment): Array<CheckInStatus> =>
  map(passenger => passenger.status)(passengers)

const requiresCheckIn = (status: CheckInStatus) =>
  includes(status, [PROVIDE_DETAILS, WAITING_FOR_DETAILS])

export const allStatusesAreCheckedIn = (statuses: Array<CheckInStatus>): boolean =>
  none(requiresCheckIn, statuses)

const statusNeverHasBoardingPass = (status: CheckInStatus) =>
  includes(status, [RESTRICTED, UNAVAILABLE, GROUND_UNAVAILABLE, MISSED, FAILED])

const allStatusesNeverHaveBoardingPasses = (statuses: Array<CheckInStatus>): boolean =>
  all(statusNeverHasBoardingPass, statuses)

export const boardingPassesNeverAvailable = (segments?: Array<CheckInSegment>): boolean =>
  compose(
    allStatusesNeverHaveBoardingPasses,
    flatten,
    map(getStatusesForSegment),
  )(segments != null ? segments : [])

export const getStatusesForPassenger = ({ segments }: CheckIn, passengerId: number) =>
  // $FlowFixMe
  segments.map(({ passengers }) => {
    const current = passengers.find(({ id }) => id === passengerId)
    return current && current.status
  })

export const hasPassengerCheckedIn = (checkIn: CheckIn, passengerId: number): boolean =>
  compose(
    allStatusesAreCheckedIn,
    partial(getStatusesForPassenger, [checkIn]),
  )(passengerId)

export const haveAllPassengersCheckedIn = (checkIn: CheckIn, passengers: Array<Passenger>) =>
  compose(
    all(partial(hasPassengerCheckedIn, [checkIn])),
    map(passenger => passenger.id),
  )(passengers)

export const createPassengerCompletionData = (
  checkIn: CheckIn,
  passengers: Array<Passenger>,
): Array<CompletionPassenger> =>
  map(passenger => ({
    ...passenger,
    isCheckedIn: hasPassengerCheckedIn(checkIn, passenger.id),
  }))(passengers)

export const haveAllCompletionPassengersCheckedIn = (passengers: Array<CompletionPassenger>) =>
  all(passenger => passenger.isCheckedIn)(passengers)

const reduceNextDeadline = (current?: Date, segment: CheckInSegment) => {
  if (!allStatusesAreCheckedIn(getStatusesForSegment(segment))) {
    if (!current || isAfter(current, segment.passportDeadlineDate)) {
      return segment.passportDeadlineDate
    }
  }
  return current
}

export const getNextPassportDeadline = ({ segments }: CheckIn) =>
  reduce(reduceNextDeadline, undefined)(segments)

const reduceNotCheckedInCount = (count: number, passenger: CompletionPassenger): number =>
  passenger.isCheckedIn ? count : inc(count)

export const getNotCheckedInCount = (passengers: Array<CompletionPassenger>): number =>
  reduce(reduceNotCheckedInCount, 0)(passengers)

export const getUTCDate = (dateString: Date = new Date()) => {
  const date = new Date(dateString)
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  )
}

export const getSpecialDocumentLabel = (passenger: Passenger, journey: JourneyType) => {
  const passengerNationality = passenger.nationality

  if (INTERNATIONAL_PASSPORT_NATIONALITIES.includes(passengerNationality)) {
    const journeyCountries = journey.getCountries()
    return journeyCountries.every(country => country === passengerNationality)
      ? __("booking.passenger.passport.label")
      : __("booking.passenger.international.label")
  }
  return null
}
